 
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CompletionMessage } from '@app-libs/model';
import { ConfigurationClientService } from '../client/configuration-client.service';
import { ApiClient } from '../client/api-client';

@Injectable({
  providedIn: 'root',
})
export class MessagesApiService extends ApiClient {
  constructor(config: ConfigurationClientService, http: HttpClient) {
    super(config, http);
  }

  override get baseUrl(): string {
    return this.config.getBackendAPIUrl() + '/messages';
  }

  // Fetch messages by conversation ID
  fetchMessagesByConversation(
    conversationId: string,
    limit = 50,
    offset = 0
  ): Observable<CompletionMessage[]> {
    return this.http.get<CompletionMessage[]>(
      `${this.baseUrl}/conversation/${conversationId}`,
      {
        params: {
          limit: limit.toString(),
          offset: offset.toString(),
        },
      }
    );
  }

  // Search messages in a conversation
  searchMessagesInConversation(
    conversationId: string,
    query: string,
    limit = 100,
    offset = 0
  ): Observable<CompletionMessage[]> {
    return this.http.get<CompletionMessage[]>(
      `${this.baseUrl}/conversation/${conversationId}/search`,
      {
        params: {
          query,
          limit: limit.toString(),
          offset: offset.toString(),
        },
      }
    );
  }

  // Create a new message
  createMessage(message: Partial<CompletionMessage>): Observable<CompletionMessage> {
    return this.http.post<CompletionMessage>(`${this.baseUrl}`, message);
  }

  // Attach files to a message
  attachFilesToMessage(
    messageId: string,
    fileIds: string[]
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/${messageId}/files`,
      { fileIds }
    );
  }

  // List files attached to a message
  listMessageFiles(messageId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/${messageId}/files`);
  }

  // Fetch recent messages for a user
  getRecentMessages(userId: string, limit = 10): Observable<CompletionMessage[]> {
    return this.http.get<CompletionMessage[]>(`${this.baseUrl}/recent`, {
      params: {
        userId,
        limit: limit.toString(),
      },
    });
  }

  // Add a reaction to a message
  addReaction(
    messageId: string,
    userId: string,
    reaction: string
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/${messageId}/reactions`,
      {
        userId,
        reaction,
      }
    );
  }

  // Get reactions for a message
  getMessageReactions(messageId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/${messageId}/reactions`);
  }

  // Mark a message as read
  markMessageAsRead(messageId: string): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      `${this.baseUrl}/${messageId}/read`,
      {}
    );
  }

  // Get token summary for a conversation
  getConversationTokenSummary(conversationId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/conversation/${conversationId}/tokens`
    );
  }

  // Search messages by keyword in a conversation
  searchMessagesByKeyword(
    conversationId: string,
    keyword: string
  ): Observable<CompletionMessage[]> {
    return this.http.get<CompletionMessage[]>(
      `${this.baseUrl}/conversation/${conversationId}/keyword-search`,
      {
        params: { keyword },
      }
    );
  }
}
